import { useEffect, useMemo, useState } from 'react'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

/**
 * Custom hook to get the correct detail row index for variable text parsing
 *
 * Uses a hybrid approach:
 * 1. Prefer activeLayerDetailRowIndex from context when valid
 * 2. Fallback to calculating from rowId when context is -1 or undefined
 * 3. Final fallback to activeLayer.index ?? 1
 *
 * @param fallbackIndex - Optional fallback index (defaults to activeLayer.index ?? 1)
 * @returns Object with detailRowIndex and strategy ('context' | 'calculated' | 'fallback')
 */
export function useDetailRowIndex(fallbackIndex?: number) {
  const { activeLayerDetailRowIndex, activeLayerDetailRowId, activeLayerTableId, activeLayer } = useActiveLayerContext()

  const { dbClient, language, userSpecific, loadedTables } = useDbClientContext()

  // State for fallback calculation
  const [calculatedRowIndex, setCalculatedRowIndex] = useState<number | undefined>(undefined)

  // Calculate fallback detailRowIndex only when activeLayerDetailRowIndex is not usable
  useEffect(() => {
    const needsFallback = activeLayerDetailRowIndex === undefined || activeLayerDetailRowIndex === -1

    if (
      needsFallback &&
      !!dbClient &&
      !!activeLayerDetailRowId &&
      !!activeLayerTableId &&
      loadedTables.has(activeLayerTableId)
    ) {
      const dbTable = dbClient.tablesMap.get(activeLayerTableId)
      if (dbTable) {
        const rowIdx = dbTable.GetRowIndexByRowId(activeLayerDetailRowId, language, userSpecific)
        if (rowIdx !== -1) {
          setCalculatedRowIndex(rowIdx)
        }
      }
    } else if (!needsFallback) {
      // Clear calculated value when context becomes valid
      setCalculatedRowIndex(undefined)
    }
  }, [
    activeLayerDetailRowIndex,
    dbClient,
    activeLayerTableId,
    language,
    loadedTables,
    activeLayerDetailRowId,
    userSpecific,
  ])

  // Get the best available row index
  const detailRowIndex = useMemo(() => {
    // 1. Use context value when valid
    if (activeLayerDetailRowIndex !== undefined && activeLayerDetailRowIndex !== -1) {
      return activeLayerDetailRowIndex
    }

    // 2. Use calculated fallback
    if (calculatedRowIndex !== undefined) {
      return calculatedRowIndex
    }

    // 3. Final fallback
    return fallbackIndex ?? activeLayer.index ?? 1
  }, [activeLayerDetailRowIndex, calculatedRowIndex, fallbackIndex, activeLayer.index])

  // Determine which strategy is being used
  const strategy = useMemo(() => {
    if (activeLayerDetailRowIndex !== undefined && activeLayerDetailRowIndex !== -1) {
      return 'context' as const
    }
    if (calculatedRowIndex !== undefined) {
      return 'calculated' as const
    }
    return 'fallback' as const
  }, [activeLayerDetailRowIndex, calculatedRowIndex])

  return {
    detailRowIndex,
    strategy,
  }
}
